/**
 * toast — one transient-feedback channel for the whole app (settings
 * redesign slice 4; replaces per-panel hand-rolled banners for the
 * "did that save?" class of feedback).
 *
 * Imperative like confirmDialog: call from any handler without threading
 * hooks through components. A single <ToastHost/> at the app root renders
 * the stack.
 *
 *   toast('Saved');                                   // success (default)
 *   toast('Couldn't save — try again', 'error');
 *   toast({ message: 'Key revoked', kind: 'info', duration: 3000 });
 *   toast({ message: 'Moved to trash', action: { label: 'Undo', run: () => restore(id) } });
 *
 * Rules of use (from the UX review): successes auto-clear; errors stay
 * longer and can be dismissed; never use a toast as the ONLY record of an
 * error a user must act on — pair it with inline state where action is
 * needed.
 */

let hostHandler = null;

/** Internal: ToastHost registers its handler here on mount. */
export function _registerToastHost(fn) {
  hostHandler = fn;
  return () => {
    if (hostHandler === fn) hostHandler = null;
  };
}

/**
 * Show a toast. Accepts (message, kind?) or an options object
 * { message, kind: 'success'|'error'|'info', duration, action?: { label, run } }.
 * An action gives the toast one button (Undo is the canonical use); a toast
 * carrying one stays up longer so the button is reachable.
 */
export function toast(opts, kind) {
  const o = typeof opts === 'string' ? { message: opts, kind } : { ...(opts || {}) };
  if (!o.message) return;
  o.kind = o.kind || 'success';
  o.duration = o.duration ?? (o.kind === 'error' ? 6000 : o.action ? 6000 : 3000);
  if (typeof hostHandler === 'function') hostHandler(o);
  // No host (unit tests): drop silently — toasts are supplementary feedback.
}

export default toast;
